<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" content-class="contact-card-actions" max-width="580">
    <v-card color="surface lighten-1">
      <v-card-title class="header">
        <EcsAvatar
          :agent-id="isAgent ? contact.id : null"
          :contact-id="isAgent ? null : contact.id"
          :alt="contact ? contact.name : ''"
          :size="52"
          :stateful="isAgent"
        />
        <div class="contact-name">
          <div class="full-name">{{ contact.name }}</div>
          <div class="details">{{ contactTypeName }}</div>
        </div>
        <div>
          <v-btn
            v-if="contact.isChatEnabled"
            icon
            class="action-btn"
            @click="
              () => {
                handleChat();
                close();
              }
            "
          >
            <v-icon class="ecs-contact-item-action-icon">
              mdi-message-text
            </v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-tabs v-if="dialog" v-model="tab" height="40" background-color="transparent">
        <v-tab>{{ $t('tabs.contact') }}</v-tab>
        <v-tab v-if="hasCrmInfo">{{ $t('tabs.history') }}</v-tab>
        <v-tab v-if="isCrmEnabled && hasCrmInfo">{{ $t('tabs.crm') }}</v-tab>
        <v-tab v-if="hasCrmInfo">{{ $t('tabs.notes') }}</v-tab>
      </v-tabs>

      <div class="contact-card-content">
        <v-tabs-items v-model="tab" class="tab-content">
          <v-tab-item>
            <div v-for="(field, index) in fields" :key="index" class="info-field">
              <div class="data">
                <div class="info-label">{{ field.label }}</div>
                <div class="info-value">{{ field.value }}</div>
              </div>
              <div v-if="action" class="actions">
                <v-btn icon class="action-btn" color="primary" @click="select(field.value)">
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
              <div v-else class="default actions">
                <v-btn
                  v-if="field.format === 'phone' && isNotEmpty(field.value)"
                  icon
                  class="action-btn"
                  @click="
                    () => {
                      close();
                      handleCall({ value: field.value });
                    }
                  "
                >
                  <v-icon>mdi-phone</v-icon>
                </v-btn>
                <v-btn
                  v-if="field.format === 'phone' && contact.isSMSEnabled && isNotEmpty(field.value)"
                  icon
                  class="action-btn"
                  @click="
                    () => {
                      close();
                      handleSMS({ value: field.value });
                    }
                  "
                >
                  <v-icon>mdi-chat-processing-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="field.format === 'email' && contact.isEmailEnabled && isNotEmpty(field.value)"
                  icon
                  class="action-btn"
                  @click="
                    () => {
                      close();
                      handleEmail({ value: field.value });
                    }
                  "
                >
                  <v-icon>mdi-email</v-icon>
                </v-btn>
                <v-btn
                  icon
                  class="action-btn"
                  @click="
                    () => {
                      close();
                      copyToClipboard(field.label, field.value);
                    }
                  "
                >
                  <v-icon> mdi-content-copy </v-icon>
                </v-btn>
              </div>
            </div>
            <!-- pre style="background-color: #0003"> {{ contact }} </pre -->
          </v-tab-item>
          <v-tab-item>
            <InteractionCrmHistory v-if="contact.id && hasCrmInfo" :contact-id="contact.id" />
          </v-tab-item>
          <v-tab-item v-if="isCrmEnabled && hasCrmInfo">
            <InteractionCrmContactTemplateSelection
              v-if="contact.id"
              :contact-id="contact.id"
              :business-process-id="selectedBusinessProcess"
            />
          </v-tab-item>
          <v-tab-item v-if="hasCrmInfo">
            <InteractionCrmNotes v-if="contact.id && hasCrmInfo" :contact-id="contact.id" />
          </v-tab-item>
        </v-tabs-items>
      </div>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close()">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsContactMixin from '@/mixins/ecs-contact-mixin/EcsContactMixin';
import { CONTACT_TYPE } from '@/constants/contactConstants';
import InteractionCrmHistory from '@/components/ecs-interaction/InteractionCrmHistory.vue';
import InteractionCrmNotes from '@/components/ecs-interaction/InteractionCrmNotes.vue';
import InteractionCrmContactTemplateSelection from '@/components/ecs-interaction/InteractionCrmContactTemplateSelection.vue';

export default {
  name: 'EcsContactCard',

  components: {
    InteractionCrmContactTemplateSelection,
    InteractionCrmNotes,
    InteractionCrmHistory,
    EcsAvatar
  },

  mixins: [EcsContactMixin],

  data() {
    return {
      contact: {},
      action: null,
      dialog: false,
      selectedBusinessProcess: null,
      tab: 0
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    isAgent() {
      return [CONTACT_TYPE.agent, CONTACT_TYPE.supervisor, CONTACT_TYPE.enterprise].includes(
        this.contact && this.contact.contactType
      );
    },

    contactTypeName() {
      return this.$t(this.contact.contactType) || this.contact.contactType;
    },

    hasCrmInfo() {
      return ![
        CONTACT_TYPE.agent,
        CONTACT_TYPE.supervisor,
        CONTACT_TYPE.enterprise,
        CONTACT_TYPE.channel,
        CONTACT_TYPE.businessProcess,
        CONTACT_TYPE.external
      ].includes(this.contact && this.contact.contactType);
    },

    isCrmEnabled() {
      return this.loggedInAgent && this.loggedInAgent.crm === 'Enabled';
    },

    fields() {
      return [
        ...(this.contact.phones || []).map(phone => ({
          label: phone.description,
          value: phone.value,
          format: 'phone'
        })),
        ...(this.contact.mobiles || []).map(mobile => ({
          label: mobile.description,
          value: mobile.value,
          format: 'phone'
        })),
        ...(this.contact.emails || []).map(email => ({
          label: email.description,
          value: email.value,
          format: 'email'
        }))
      ];
    }
  },

  created() {
    this.$root.$on('open-contact-card-actions', this.open);
    this.$root.$on('close-contact-card-actions', this.close);
  },

  destroyed() {
    this.$root.$off('open-contact-card-actions', this.open);
    this.$root.$off('close-contact-card-actions', this.close);
  },

  methods: {
    open({ contact, action = null, tab = null, businessProcessId = null }) {
      this.dialog = true;
      this.contact = contact;
      this.action = action || this.action;
      this.selectedBusinessProcess = businessProcessId;
      this.setTab(tab);
    },

    close() {
      this.dialog = false;
      this.contact = {};
      this.action = null;
      this.$emit('close-action-selected', null);
    },

    select({ action, target }) {
      this.dialog = false;
      this.$emit('close-action-selected', { action, target });
    },

    setTab(tabName) {
      switch (tabName) {
        case 'contact':
          this.tab = 0;
          break;
        case 'history':
          this.tab = 1;
          break;
        case 'crm':
          this.tab = 2;
          break;
        case 'notes':
          this.tab = 3;
          break;
        default:
          this.tab = 0;
          break;
      }
    },

    isNotEmpty(value) {
      return value !== '';
    },

    isValidEndpoint: value => (+value >= 200 && +value <= 999) || +value === 100,

    isValidCompanyContact(value) {
      const companyRegExp = /([^\s]+)@([^\s]+)/g;
      return companyRegExp.test(value);
    },

    copyToClipboard(label, value) {
      navigator.clipboard
        .writeText(value || '')
        .then(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('copyToClipboardSuccess', { label }),
            type: 'success'
          });
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped src="./scoped.scss" lang="scss" />

<style lang="scss">
.contact-card-actions .theme--dark.v-tabs-items {
  background-color: transparent;
}
.contact-card-actions .v-dialog > .v-card > .v-card__title {
  padding: 12px 20px;
}
</style>
